import { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import type { components } from "@dolfin/api-admin";
import { useDolfinClient } from "@/components/DolfinProvider";
import { InfoHint } from "@/components/InfoHint";
import { Timeline, type TimelineEntry } from "@/components/PageUi";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { formatWhatsAppNumber } from "@/lib/whatsapp";

type Message = components["schemas"]["InvoiceWhatsAppMessageResponse"];
type Status = components["schemas"]["WhatsAppMessageStatus"];

const KIND_LABELS: Record<components["schemas"]["WhatsAppMessageKind"], string> = {
  InvoiceSend: "Invoice",
  InvoiceReminder: "Reminder",
  BillNeedsApproval: "Approval request",
};

// Why a message was skipped, in the reader's words. Every skip sent the email instead.
const SKIP_REASONS: Record<string, string> = {
  demo: "Not sent - demo",
  connection_not_connected: "WhatsApp is not connected, sent by email",
  module_disabled: "WhatsApp is not included in your plan, sent by email",
  event_disabled: "This message is switched off for WhatsApp, sent by email",
  no_whatsapp_number: "The customer has no WhatsApp number, sent by email",
  opted_out: "The customer opted out, sent by email",
  no_consent: "No WhatsApp consent recorded, sent by email",
  template_not_approved: "Message template still in review with Meta, sent by email",
  no_link: "No payment link to send, sent by email",
};

const STATUS_STYLES: Record<Status, string> = {
  Skipped: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400",
  Sent: "border-border bg-muted text-muted-foreground",
  Delivered: "border-accent-blue/30 bg-accent-blue/10 text-accent-blue",
  Read: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  Failed: "border-destructive/30 bg-destructive/10 text-destructive",
};

function entry(m: Message, i: number): TimelineEntry {
  const status = m.status ?? "Sent";
  const to = formatWhatsAppNumber(m.to);
  const detail =
    status === "Skipped"
      ? (SKIP_REASONS[m.reason ?? ""] ?? "Not sent on WhatsApp, sent by email")
      : status === "Failed"
        ? `WhatsApp could not deliver it${m.reason ? `: ${m.reason}` : "."}`
        : to
          ? `To ${to}`
          : null;
  return {
    id: m.id ?? String(i),
    title: (
      <span className="flex items-center gap-2">
        {KIND_LABELS[m.kind ?? "InvoiceSend"]}
        <Badge variant="outline" className={STATUS_STYLES[status]}>
          {status}
        </Badge>
      </span>
    ),
    // When it reached its current status, so the rail reads as "where it is now".
    at: m.readAt ?? m.deliveredAt ?? m.failedAt ?? m.sentAt ?? m.createdAt,
    detail,
  };
}

/** A side panel: a failed read says so here and leaves the rest of the page alone. */
export function InvoiceWhatsAppMessages({ invoiceId, refreshKey }: { invoiceId: string; refreshKey?: string }) {
  const api = useDolfinClient();
  const [messages, setMessages] = useState<Message[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  // Only the latest read is applied: a slow earlier one (a refresh, then another) must not overwrite it.
  const latest = useRef(0);
  const load = useCallback(async () => {
    const request = ++latest.current;
    setLoading(true);
    const { data, error } = await api.GET("/v1/invoices/{id}/whatsapp-messages", {
      params: { path: { id: invoiceId } },
    });
    if (request !== latest.current) return;
    setFailed(!!error);
    if (!error) setMessages(data ?? []);
    setLoading(false);
  }, [api, invoiceId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-1 text-base">
          WhatsApp
          <InfoHint label="More about WhatsApp messages">
            Invoices and reminders go on WhatsApp to customers who prefer it and have agreed. Whenever WhatsApp
            cannot be used, the customer gets the email instead.
          </InfoHint>
        </CardTitle>
        <CardAction>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label="Refresh WhatsApp messages"
            disabled={loading}
            onClick={() => void load()}
          >
            {loading ? <Spinner className="size-3.5" /> : <RefreshCw className="size-3.5" />}
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        {failed ? (
          <p className="text-sm text-destructive">Couldn't load WhatsApp messages.</p>
        ) : messages === null ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : messages.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing sent on WhatsApp for this invoice.</p>
        ) : (
          <Timeline entries={messages.map(entry)} />
        )}
      </CardContent>
    </Card>
  );
}
